'use client';

import { useEffect } from 'react';
import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { Button } from '../buttons';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../forms';
import { useToast } from '../../hooks';
import { tryCatchAsync } from '@shared/utils';
import type { FormDialogProps } from '../../types/FormDialogProps';

/**
 * @group Components
 * @summary <blockquote>FormDialog component for dynamic form dialogs with validation.</blockquote>
 * @example
 * ```tsx
 * <FormDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Edit User"
 *   fields={fields}
 *   schema={schema}
 *   onSubmit={handleSubmit}
 * />
 * ```
 */
export function FormDialog<TData>({
  open,
  onOpenChange,
  title,
  fields,
  schema,
  data,
  onSubmit,
}: FormDialogProps<TData>): JSX.Element {
  const { toast } = useToast();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  useEffect(() => {
    if (data) {
      form.reset(data);
    } else {
      form.reset({});
    }
  }, [data, form]);

  const handleSubmit = async (formData: TData): Promise<void> => {
    const result = await tryCatchAsync(async () => {
      await onSubmit(formData);
      onOpenChange(false);
      form.reset();
    });

    if (result.isErr) {
      toast({
        title: 'Error',
        description: 'Failed to save data',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          {/* @ts-ignore */}
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {fields.map((field) => {
              const Component = field.component || 'input';
              return (
                <FormField
                  key={field.name}
                  control={form.control}
                  // @ts-ignore
                  name={field.name}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormLabel>{field.label}</FormLabel>
                      <FormControl>
                        <Component
                          {...formField}
                          type={field.type || 'text'}
                          placeholder={field.placeholder}
                        />
                      </FormControl>
                      {field.description && (
                        <FormDescription>{field.description}</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
            <DialogFooter>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
} 